import React, { Suspense, lazy } from "react";
import { createRoot } from "react-dom/client";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { WalletProvider } from "./contexts/WalletContext";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import SessionEndedDialog from "./components/auth/SessionEndedDialog";
import PaymentSuccess from "./components/PaymentSuccess";
import PaymentCancelled from "./components/PaymentCancelled";
import "./styles/global.scss";
import "./styles/index.scss";
import "./styles/index.css";
import "./styles/AuthCallback.scss";
import "./styles/LoadingSpinner.scss";

const AuthCallback = lazy(() => import("./components/auth/AuthCallback"));
const AddCompany = lazy(() => import("./components/AddCompany"));
const EditCompany = lazy(() => import("./components/EditCompany"));
const SignalValidate = lazy(() => import("./components/SignalValidate"));
const LandingPage = lazy(() => import("./components/LandingPage"));
const AdminFunctions = lazy(() => import("./components/admin/AdminFunctions"));
const CompanyDetails = lazy(() => import("./components/CompanyDetails"));
const TransactionHistory = lazy(() => import("./components/admin/TransactionHistory"));
const Simulator = lazy(() => import("./components/admin/Simulator"));
const SignalCard = lazy(() => import("./components/SignalCard"));
const SignalScan = lazy(() => import("./components/SignalScan"));
// SignalScan ME is the chat intake now. The previous table-and-form page is
// `SignalScan_MultiEntity.tsx`; it is no longer routed.
const SignalScanMEChat = lazy(() => import("./components/SignalScanMEChat"));
const CreditManagement = lazy(() => import("./components/admin/CreditManagement"));
const SignalCardDemoManagement = lazy(() => import("./components/admin/SignalCardDemoManagement"));
const ProfilePage = lazy(() => import("./components/ProfilePage"));

// if (process.env.REACT_APP_ENV === "dev") {
//   const whyDidYouRender = require("@welldone-software/why-did-you-render");
//   whyDidYouRender(React, { trackHooks: true });
// }

// Note: These imports need to be implemented based on your app structure
// const { store } = configureStore();

const container = document.getElementById("root");
const root = createRoot(container!);

const AppShellLoader = () => (
  <div
    style={{
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: "1rem",
      background: "linear-gradient(135deg, var(--primary-light, #D5ECE8) 0%, var(--accent-light, #E8F4F8) 100%)",
      color: "var(--text-dark, #2C3E50)",
    }}
  >
    <div className="auth-spinner"></div>
    <p>Loading IntegrisIQ...</p>
  </div>
);

const AuthenticatedAppRoutes = () => {
  return (
    <AuthProvider>
      {/* The wallet, the catalogue and the Buy Credits dialog, shared by every page below and
          loaded once per visit rather than by each page's own header. It waits for sign-in
          before calling anything. */}
      <WalletProvider>
      {/* Over whatever page is open when the sign-in runs out; the page stays as it is. */}
      <SessionEndedDialog />
      <Suspense fallback={<AppShellLoader />}>
        <Routes>
          <Route 
            path="/" 
            element={
              <ProtectedRoute>
                <LandingPage />
              </ProtectedRoute>
            } 
          />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route
            path="/signal-card"
            element={
              <ProtectedRoute>
                <SignalCard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/signal-scan"
            element={
              <ProtectedRoute>
                <SignalScan />
              </ProtectedRoute>
            }
          />
          <Route
            path="/signal-scan-multi-entity"
            element={
              <ProtectedRoute>
                <SignalScanMEChat />
              </ProtectedRoute>
            }
          />
          {/* The same page addressed by the run it is showing, so a reload or a bookmark
              keeps it. Two explicit routes rather than an optional segment: the optional
              form matches the bare path too and makes the param silently undefined. */}
          <Route
            path="/signal-scan-multi-entity/:runId"
            element={
              <ProtectedRoute>
                <SignalScanMEChat />
              </ProtectedRoute>
            }
          />
          <Route
            path="/signal-validate"
            element={
              <ProtectedRoute>
                <SignalValidate />
              </ProtectedRoute>
            }
          />
          <Route
            path="/user/transactions"
            element={
              <ProtectedRoute>
                <TransactionHistory />
              </ProtectedRoute>
            }
          />
          <Route
            path="/add-company"
            element={
              <ProtectedRoute>
                <AddCompany />
              </ProtectedRoute>
            }
          />
          <Route
            path="/edit-company/:id"
            element={
              <ProtectedRoute>
                <EditCompany />
              </ProtectedRoute>
            }
          />
          <Route
            path="/company/:id"
            element={
              <ProtectedRoute>
                <CompanyDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminFunctions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/simulator"
            element={
              <ProtectedRoute>
                <Simulator />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/credits"
            element={
              <ProtectedRoute>
                <CreditManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/signal-card-demo-management"
            element={
              <ProtectedRoute>
                <SignalCardDemoManagement />
              </ProtectedRoute>
            }
          />

          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            }
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      </WalletProvider>
    </AuthProvider>
  );
};

const AppRoutes = () => {
  return (
    <Routes>
      <Route path="/payment-success/*" element={<PaymentSuccess />} />
      <Route path="/payment-cancelled/*" element={<PaymentCancelled />} />
      <Route path="/*" element={<AuthenticatedAppRoutes />} />
    </Routes>
  );
};

const App = () => {
  return (
    <Router>
      <AppRoutes />
    </Router>
  );
};

root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
